import axios from 'axios';

import { ROCKET_CHAT_URL } from 'constants/config';
import { store } from 'store';
import { getLocalStorage, setLocalStorage, removeLocalStorage, MACRO_NAME, MACRO_BACKUP_FLAG_NAME } from 'utils/localStorage';
import { jwtDecoder, validateToken } from 'utils/auth/tokenUtils';
import { getSupportRegion } from 'utils/snippet';
import { encodeAuthentication, type Authentication } from 'utils/socket/authentication';
import { logLevel } from 'utils/consoleLevel';

interface BackupArgs {
  region: string
  authentication: Authentication
}

// 웹소켓 URL에서 REST 오리진(https://host)을 유도한다.
// API 경로는 항상 호스트 루트 기준이므로 웹소켓 URL의 경로(/ws 유무, 꼬리 슬래시)는 무시한다.
const restOrigin = (region: string) => {
  const url = new URL(ROCKET_CHAT_URL.replace(':region', region));
  const protocol = url.protocol === 'ws:' ? 'http:' : 'https:';
  return `${protocol}//${url.host}`;
};

// 슬롯 맵에 실제 내용이 있는 문구가 하나라도 있는지 확인한다.
const hasContent = (macros: Record<string, string>) =>
  Object.values(macros).some((phrase) => typeof phrase === 'string' && phrase.trim() !== '');

// 현재 로그인 세션의 인증 정보를 소켓 연결과 동일한 우선순위(QR → Biz → OIDC)로 얻는다.
const currentAuthentication = (): Authentication | null => {
  const { storeAccessToken, bizToken, oidcAccessToken } = store.getState().authStore;

  const decodedAccToken = jwtDecoder(storeAccessToken);
  if (decodedAccToken && validateToken(decodedAccToken)) {
    return { qrToken: storeAccessToken };
  }
  if (bizToken) {
    return { bizToken };
  }
  if (oidcAccessToken) {
    return { accessToken: oidcAccessToken };
  }
  return null;
};

// 자주쓰는문구(매크로)를 서버에 백업한다. flag와 무관하게 즉시 호출된다.
// 서버가 이미 복구된 유저로 판단해 무시한 경우(IGNORED_ALREADY_RESTORED)도
// 성공으로 간주해 flag를 저장하고 이후의 백업 시도를 중단한다.
// 실패 시 flag를 제거해 다음 진입 시 재시도되도록 한다.
export const backupMacro = async (args?: BackupArgs) => {
  const macros = getLocalStorage(MACRO_NAME);
  if (!macros || !hasContent(macros)) {
    return;
  }

  const region = args?.region ?? getSupportRegion();
  const authentication = args?.authentication ?? currentAuthentication();
  if (!authentication) {
    return;
  }

  try {
    const queryParams = encodeAuthentication(authentication);
    await axios.post(
      `${restOrigin(region)}/api/users/legacy-web-macro-backup?${queryParams}`,
      { macros },
      { headers: { 'Content-Type': 'application/json' } },
    );
    setLocalStorage({ key: MACRO_BACKUP_FLAG_NAME, value: true });
  } catch (error) {
    removeLocalStorage(MACRO_BACKUP_FLAG_NAME);
    logLevel.log(error);
  }
};

// 진입·탭 활성화 시 체크: 백업 flag가 없을 때만 백업을 시도한다.
// args를 생략하면 스토어에서 현재 세션의 region/인증 정보를 얻는다.
export const backupMacroOnEntry = (args?: BackupArgs) => {
  if (getLocalStorage(MACRO_BACKUP_FLAG_NAME) === true) {
    return;
  }
  void backupMacro(args);
};
